/**
 * Auth Store - Zustand store for authentication state
 * Provides reactive state management for auth in React components
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { User } from 'oidc-client-ts'
import { authService } from './auth-service'
import { isKeycloakConfigured } from './keycloak-config'
import { decodeJwtPayload } from './jwt'

// UserContext type and fetch function (inline to avoid external dependency)
export interface UserContext {
  userId: string
  email: string
  organizationId: string | null
  organizationName?: string | null
  orgRole: string | null
  organizationRole?: string | null
  memberType?: string | null
  memberStatus?: string | null      // User's membership status: ACTIVE, INVITED, SUSPENDED
  permissions: string[]
  approvalStatus?: string           // Organization's approval status: PENDING_APPROVAL, APPROVED, REJECTED
}

// Use runtime config for API configuration (works in both dev and production)
const getApiBase = (): string => {
  // Runtime config from container (highest priority - production/staging)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.PUBLIC_API_BASE_URL) {
    // Remove /v1 suffix if present (backend uses /api not /api/v1)
    return window.__RUNTIME_CONFIG__.PUBLIC_API_BASE_URL.replace(/\/v1$/, '')
  }
  // Local development fallback (only when actually on localhost)
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return 'http://localhost:8080/api'
  }
  // Production fallback (relative path)
  return '/api'
}

async function fetchUserContext(): Promise<UserContext> {
  const token = await authService.getAccessToken()
  if (!token) {
    throw new Error('No access token available')
  }

  const buildFallbackContext = (): UserContext => {
    const payload = decodeJwtPayload(token) as Record<string, unknown> | null
    const fallbackOrgRole =
      payload?.organizationRole ||
      payload?.organization_role ||
      payload?.orgRole ||
      payload?.org_role ||
      payload?.portalType ||       // portalType as role hint
      payload?.portal_type
    const fallbackApprovalStatus =
      payload?.approvalStatus ||
      payload?.approval_status ||
      payload?.organizationApprovalStatus ||
      payload?.verificationStatus ||
      payload?.verification_status
    // Support multiple field names - organizationBusinessId is the new standard per CLAUDE.md
    const fallbackOrgId = 
      payload?.organizationBusinessId ||
      payload?.organization_business_id ||
      payload?.organizationId ||
      payload?.organization_id
    return {
      userId: String(payload?.sub || ''),
      email: String(payload?.email || ''),
      organizationId: fallbackOrgId ? String(fallbackOrgId) : null,
      organizationName: payload?.organizationName ? String(payload.organizationName) : null,
      orgRole: fallbackOrgRole ? String(fallbackOrgRole) : null,
      organizationRole: fallbackOrgRole ? String(fallbackOrgRole) : null,
      memberType: payload?.memberType ? String(payload.memberType) : null,
      permissions: [],
      approvalStatus: fallbackApprovalStatus ? String(fallbackApprovalStatus) : undefined,
    }
  }

  const apiBase = getApiBase()
  const response = await fetch(`${apiBase}/user/context`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    // Return default context if endpoint not available
    return buildFallbackContext()
  }

  const contentType = response.headers.get('content-type') || ''
  const rawText = await response.text()
  if (!rawText || !contentType.includes('application/json')) {
    return buildFallbackContext()
  }
  const context = JSON.parse(rawText)

  // Some environments do not include org approval status in /user/context.
  // Fetch organization info and use it as the source of truth when available.
  let approvalStatus: string | undefined = context?.approvalStatus ?? undefined
  try {
    const orgRes = await fetch(`${apiBase}/organizations/my`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (orgRes.ok) {
      const orgContentType = orgRes.headers.get('content-type') || ''
      const orgRawText = await orgRes.text()
      if (orgRawText && orgContentType.includes('application/json')) {
        const org = JSON.parse(orgRawText) as {
          approvalStatus?: string
          verificationStatus?: string
          name?: string
        }
        const statusFromOrg = org?.approvalStatus ?? org?.verificationStatus
        if (statusFromOrg) approvalStatus = String(statusFromOrg)
        if (org?.name && context) {
          context.organizationName = org.name
        }
      }
    } else if (orgRes.status === 409 || orgRes.status === 401) {
      // Tolerant fallback: don't break auth context on conflict/unauthorized
      const body = await orgRes.text()
      console.warn('[auth-store] organizations/my', orgRes.status, orgRes.statusText, body)
    }
  } catch {
    // Ignore: keep approvalStatus from /user/context or JWT fallback.
  }

  return {
    ...context,
    organizationRole: context?.organizationRole ?? context?.orgRole ?? null,
    orgRole: context?.orgRole ?? context?.organizationRole ?? null,
    memberType: context?.memberType ?? null,
    approvalStatus,
  }
}

const REFRESH_COOLDOWN_MS = 5000
let refreshInFlight = false
let lastRefreshAttempt = 0

const extractRolesFromUser = (user: User | null): string[] => {
  const roles = new Set<string>()
  const addRole = (role: string | undefined | null) => {
    if (!role) return
    roles.add(role.toLowerCase())
  }

  // 1) Roles from profile (userinfo or id_token)
  const profileRealmRoles = (user?.profile as { realm_access?: { roles?: string[] } })?.realm_access
    ?.roles
  if (Array.isArray(profileRealmRoles)) profileRealmRoles.forEach(r => addRole(r))

  // 2) Roles from access_token JWT (covers setups where userinfo omits realm_access)
  const accessToken = user?.access_token
  const payload = accessToken ? decodeJwtPayload(accessToken) : null

  const tokenRealmRoles = (payload as { realm_access?: { roles?: string[] } })?.realm_access?.roles
  if (Array.isArray(tokenRealmRoles)) tokenRealmRoles.forEach(r => addRole(r))

  // 3) Client roles from resource_access.<client>.roles (very common in Keycloak)
  const resourceAccess = (payload as { resource_access?: Record<string, { roles?: string[] }> })
    ?.resource_access
  if (resourceAccess && typeof resourceAccess === 'object') {
    Object.values(resourceAccess).forEach(client => {
      if (Array.isArray(client?.roles)) client.roles.forEach(r => addRole(r))
    })
  }

  // 4) Groups claim (often easier than roles, depending on Keycloak client scope/mappers)
  const groups = (payload as { groups?: string[] })?.groups
  if (Array.isArray(groups)) {
    groups.forEach(g => addRole(g))
    // Infer default access-level roles from group membership
    // Per CLAUDE.md: clients = hospitals/clinics, providers = suppliers
    const normalized = groups.map(g => g.replace(/^\//, '').toLowerCase())
    if (normalized.includes('clients')) addRole('client-viewer')
    if (normalized.includes('providers')) addRole('provider-viewer')
    if (normalized.includes('backoffice')) addRole('wasilah-user')
  }

  // 5) Normalize common role prefixes (Spring Security/others)
  Array.from(roles).forEach(r => {
    if (r.startsWith('role_')) addRole(r.substring('role_'.length))
  })

  return Array.from(roles)
}

/**
 * Auth state interface
 */
export interface AuthState {
  // State
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | null

  // User context from backend (organization, permissions)
  userContext: UserContext | null
  isLoadingContext: boolean

  // Computed from user profile
  userProfile: {
    id: string | null
    email: string | null
    name: string | null
    firstName: string | null
    lastName: string | null
    roles: string[]
    organizationId: string | null
    organizationRole: string | null
  }

  // Actions
  initialize: () => Promise<void>
  login: (redirectPath?: string) => Promise<void>
  register: (redirectPath?: string) => Promise<void>
  logout: () => Promise<void>
  handleCallback: () => Promise<User | null>
  /** `force` skips the cooldown: use it after changes that alter the token (name, roles, organization). */
  refreshToken: (opts?: { force?: boolean }) => Promise<void>
  fetchUserContext: () => Promise<void>
  clearError: () => void
}

/**
 * Extract organization info from JWT token
 * Supports multiple field names for backward compatibility:
 * - organizationBusinessId (new Keycloak standard per CLAUDE.md)
 * - organizationId / organization_id (legacy)
 */
const extractOrganizationInfo = (
  user: User | null
): { organizationId: string | null; organizationRole: string | null } => {
  if (!user?.access_token) {
    return { organizationId: null, organizationRole: null }
  }

  const payload = decodeJwtPayload(user.access_token)
  if (!payload) {
    return { organizationId: null, organizationRole: null }
  }

  // Support multiple field names - organizationBusinessId is the new standard
  const organizationId =
    (payload.organizationBusinessId as string) ||
    (payload.organization_business_id as string) ||
    (payload.organizationId as string) || 
    (payload.organization_id as string) || 
    null
  const organizationRole =
    (payload.organizationRole as string) || 
    (payload.organization_role as string) ||
    (payload.portalType as string) ||  // portalType can be used as role hint
    (payload.portal_type as string) ||
    null

  // Store in sessionStorage for components that need it
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('auth_token', user.access_token)
    if (organizationId) {
      sessionStorage.setItem('organizationId', organizationId)
    } else {
      sessionStorage.removeItem('organizationId')
    }
    if (organizationRole) {
      sessionStorage.setItem('organizationRole', organizationRole)
    } else {
      sessionStorage.removeItem('organizationRole')
    }
  }

  return { organizationId, organizationRole }
}

/**
 * Extract user profile from OIDC User object
 */
const extractUserProfile = (user: User | null) => {
  const { organizationId, organizationRole } = extractOrganizationInfo(user)
  return {
    id: user?.profile?.sub ?? null,
    email: user?.profile?.email ?? null,
    name: user?.profile?.name ?? null,
    firstName: (user?.profile?.given_name as string) ?? null,
    lastName: (user?.profile?.family_name as string) ?? null,
    roles: extractRolesFromUser(user),
    organizationId,
    organizationRole,
  }
}

/**
 * Check if mocking is enabled (same logic as MSW)
 */
const isMockingEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  const runtimeConfig = window.__RUNTIME_CONFIG__ || {}
  return (
    runtimeConfig.PUBLIC_ENABLE_MOCKS === 'true' ||
    localStorage.getItem('enableMocks') === 'true' ||
    import.meta.env.VITE_ENABLE_MOCKS === 'true' ||
    import.meta.env.PUBLIC_ENABLE_MOCKS === 'true'
  )
}

/**
 * Get mock user data for development
 */
const getMockAuthData = async () => {
  // Dynamic import to avoid bundling mock data in production
  const { createMockOidcUser, getCurrentMockUserPreset } = await import('~/mocks/data/mock-user')
  return {
    mockUser: createMockOidcUser(),
    mockUserContext: getCurrentMockUserPreset(),
  }
}

/**
 * Auth store using Zustand
 */
export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    userContext: null,
    isLoadingContext: false,
    userProfile: extractUserProfile(null),

    /**
     * Initialize auth service and restore session
     * When mocking is enabled, bypasses Keycloak and uses mock data
     */
    initialize: async () => {
      try {
        set({ isLoading: true, error: null })

        // Check if mocking is enabled - bypass real auth
        if (import.meta.env.DEV && isMockingEnabled()) {
          console.log('[Auth] Mock mode enabled - using mock authentication')
          const { mockUser, mockUserContext } = await getMockAuthData()

          // Store mock token in sessionStorage for API calls
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('auth_token', mockUser.access_token)
            if (mockUserContext.organizationId) {
              sessionStorage.setItem('organizationId', mockUserContext.organizationId)
            }
            if (mockUserContext.orgRole) {
              sessionStorage.setItem('organizationRole', mockUserContext.orgRole)
            }
          }

          set({
            user: mockUser as unknown as User,
            isAuthenticated: true,
            userProfile: {
              id: mockUser.profile.sub,
              email: mockUser.profile.email,
              name: mockUser.profile.name,
              firstName: mockUser.profile.given_name,
              lastName: mockUser.profile.family_name,
              roles: ['user', 'wasilah-admin', 'provider-admin'],
              organizationId: mockUserContext.organizationId,
              organizationRole: mockUserContext.orgRole,
            },
            userContext: mockUserContext,
            isLoading: false,
            isLoadingContext: false,
          })
          return
        }

        await authService.initialize()

        // Setup event listeners
        authService.on('userLoaded', user => {
          set({
            user: user ?? null,
            isAuthenticated: !!user && !user.expired,
            userProfile: extractUserProfile(user ?? null),
          })
          if (user && !user.expired) {
            get()
              .fetchUserContext()
              .catch(() => undefined)
          }
        })

        authService.on('userUnloaded', () => {
          set({
            user: null,
            isAuthenticated: false,
            userProfile: extractUserProfile(null),
          })
        })

        authService.on('accessTokenExpired', () => {
          // Token expired, try to refresh using refresh_token grant
          get()
            .refreshToken()
            .catch(() => undefined)
        })

        authService.on('accessTokenExpiring', () => {
          // Token expiring soon, proactively refresh
          get()
            .refreshToken()
            .catch(() => undefined)
        })

        // Get current user
        const user = await authService.getUser()
        set({
          user,
          isAuthenticated: !!user && !user.expired,
          userProfile: extractUserProfile(user),
          isLoading: false,
        })
        if (user && !user.expired) {
          await get().fetchUserContext()
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error('Auth initialization failed'),
          isLoading: false,
        })
      }
    },

    /**
     * Redirect to Keycloak login
     */
    login: async (redirectPath?: string) => {
      try {
        set({ isLoading: true, error: null })

        // Ensure AuthService is initialized before login
        await authService.initialize()

        // Store the redirect path for after login
        if (redirectPath && typeof window !== 'undefined') {
          sessionStorage.setItem('auth_redirect_path', redirectPath)
        }

        // Force showing the login screen / account selection (prevents "sticky" SSO during dev)
        await authService.login({
          extraQueryParams: {
            prompt: 'login',
          },
        })
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error('Login failed'),
          isLoading: false,
        })
      }
    },

    /**
     * Redirect to Keycloak registration (kc_action=register).
     * New users land without a portal role → onboarding (Client/Provider).
     */
    register: async (redirectPath?: string) => {
      try {
        set({ isLoading: true, error: null })

        await authService.initialize()

        if (redirectPath && typeof window !== 'undefined') {
          sessionStorage.setItem('auth_redirect_path', redirectPath)
        }

        await authService.register()
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error('Registration failed'),
          isLoading: false,
        })
      }
    },

    /**
     * Logout from Keycloak
     */
    logout: async () => {
      try {
        set({ isLoading: true, error: null })

        // Ensure AuthService is initialized before logout
        await authService.initialize()

        // Clear local session first (prevents "stuck" users due to cached OIDC state)
        await authService.removeUser()

        // Clear organization info from sessionStorage
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('organizationId')
          sessionStorage.removeItem('organizationRole')
          sessionStorage.removeItem('auth_token')
        }

        set({
          user: null,
          isAuthenticated: false,
          userProfile: extractUserProfile(null),
        })

        // Then perform Keycloak end-session redirect (kills SSO session)
        await authService.logout()
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error('Logout failed'),
          isLoading: false,
        })
      }
    },

    /**
     * Handle login callback
     */
    handleCallback: async () => {
      try {
        set({ isLoading: true, error: null })

        // Ensure AuthService is initialized before handling callback
        await authService.initialize()

        const user = await authService.handleLoginCallback()
        set({
          user,
          isAuthenticated: true,
          userProfile: extractUserProfile(user),
          isLoading: false,
        })
        return user
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error('Callback failed'),
          isLoading: false,
        })
        return null
      }
    },

    /**
     * Refresh access token using refresh_token grant (no iframe)
     */
    refreshToken: async (opts?: { force?: boolean }) => {
      if (refreshInFlight) return
      const now = Date.now()
      if (!opts?.force && now - lastRefreshAttempt < REFRESH_COOLDOWN_MS) return
      refreshInFlight = true
      lastRefreshAttempt = now
      try {
        const user = await authService.refreshToken()
        if (user) {
          set({
            user,
            isAuthenticated: true,
            userProfile: extractUserProfile(user),
          })
          return
        }
        const error = new Error('Session expired. Please log in again.')
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          userProfile: extractUserProfile(null),
          error,
        })
        if (typeof window !== 'undefined' && isKeycloakConfigured()) {
          const redirectPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          await get().login(redirectPath)
        }
      } catch (error) {
        // If refresh fails, user needs to re-authenticate
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          userProfile: extractUserProfile(null),
          error: error instanceof Error ? error : new Error('Token refresh failed'),
        })
        if (typeof window !== 'undefined' && isKeycloakConfigured()) {
          const redirectPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          await get().login(redirectPath)
        }
      } finally {
        refreshInFlight = false
      }
    },

    /**
     * Fetch user context from backend after authentication
     */
    fetchUserContext: async () => {
      const { isAuthenticated, user } = get()

      if (!isAuthenticated || !user) {
        set({ userContext: null })
        return
      }

      try {
        set({ isLoadingContext: true })
        const context = await fetchUserContext()
        const normalizedContext = {
          ...context,
          organizationRole: context.organizationRole ?? context.orgRole ?? null,
          orgRole: context.orgRole ?? context.organizationRole ?? null,
        }

        // Update sessionStorage with backend-authoritative data
        if (typeof window !== 'undefined') {
          if (normalizedContext.organizationId) {
            sessionStorage.setItem('organizationId', normalizedContext.organizationId)
          }
          if (normalizedContext.orgRole) {
            sessionStorage.setItem('organizationRole', normalizedContext.orgRole)
          }
          if (normalizedContext.approvalStatus) {
            sessionStorage.setItem('approvalStatus', normalizedContext.approvalStatus)
          }
          if (normalizedContext.permissions) {
            sessionStorage.setItem('permissions', JSON.stringify(normalizedContext.permissions))
          }
        }

        set({
          userContext: normalizedContext,
          isLoadingContext: false,
          // Update userProfile with backend data
          userProfile: {
            ...get().userProfile,
            organizationId: normalizedContext.organizationId,
            organizationRole: normalizedContext.orgRole,
          },
        })
      } catch {
        set({
          userContext: null,
          isLoadingContext: false,
        })
      }
    },

    /**
     * Clear error state
     */
    clearError: () => set({ error: null }),
  }))
)

/**
 * Selectors for common auth state access
 */
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated
export const selectUser = (state: AuthState) => state.user
export const selectUserProfile = (state: AuthState) => state.userProfile
export const selectIsLoading = (state: AuthState) => state.isLoading
export const selectError = (state: AuthState) => state.error
export const selectUserRoles = (state: AuthState) => state.userProfile.roles

/**
 * Selectors for user context
 */
export const selectUserContext = (state: AuthState) => state.userContext
export const selectApprovalStatus = (state: AuthState) => state.userContext?.approvalStatus ?? null
export const selectPermissions = (state: AuthState) => state.userContext?.permissions ?? []
export const selectOrganizationName = (state: AuthState) =>
  state.userContext?.organizationName ?? null
export const selectIsLoadingContext = (state: AuthState) => state.isLoadingContext

/**
 * Check if user has a specific permission
 */
export const selectHasPermission = (permission: string) => (state: AuthState) =>
  state.userContext?.permissions.includes(permission) ?? false

/**
 * Check if organization is approved
 * Note: Backend returns lowercase status, so we check case-insensitively
 */
export const selectIsOrganizationApproved = (state: AuthState) =>
  state.userContext?.approvalStatus?.toUpperCase() === 'APPROVED'

/**
 * Check if organization is pending approval
 * Note: Backend returns lowercase status, so we check case-insensitively
 */
export const selectIsOrganizationPending = (state: AuthState) =>
  state.userContext?.approvalStatus?.toUpperCase() === 'PENDING_APPROVAL'
